import React, { useState, useEffect, useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Search, MoreVertical, Copy, Loader, Loader2 } from "lucide-react";
import Dropdown from "../../../components/ui/Dropdown";
import Pagination from "../../../components/ui/Pagination";
import { toast } from "react-hot-toast";

import {
  useReserveTrans,
  useApproveReserveTrans,
  useExtendReserveTrans,
  useCancelReserveTransaction
} from "../hooks/useReserveTrans";

import ReserveTransDetails from "./ReserveTransDetails";
import ExtendReserveModal from "./ExtendReserveModal";
import CancelReasonModal from "./CancelReasonModal";

export function ReserveTransTable() {
  const [globalFilter, setGlobalFilter] = useState("");
  const [debouncedFilter, setDebouncedFilter] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [selectedTrans, setSelectedTrans] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const [extendTarget, setExtendTarget] = useState(null);
  const [isExtendOpen, setIsExtendOpen] = useState(false);

  const [cancelTarget, setCancelTarget] = useState(null);
  const [isCancelOpen, setIsCancelOpen] = useState(false);

  const [loadingRowId, setLoadingRowId] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilter(globalFilter);
      if (globalFilter !== debouncedFilter) {
        setPage(1);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [globalFilter, debouncedFilter]);

  const { data, isLoading, isError, error } = useReserveTrans(page, limit, debouncedFilter);
  const { mutate: approveTrans, isPending: isApproving } = useApproveReserveTrans();
  const { mutate: extendTrans, isPending: isExtending } = useExtendReserveTrans();
  const { mutate: cancelTrans, isPending: isCanceling } = useCancelReserveTransaction();

  const handleViewDetails = (trans) => {
    setSelectedTrans(trans);
    setIsDetailsOpen(true);
  };

  const handleOpenExtend = (trans) => {
    setExtendTarget(trans);
    setIsExtendOpen(true);
  };

  const handleApprove = (trans) => {
    if (!trans) return;
    setLoadingRowId(trans.uuid);
    approveTrans(trans.uuid, {
      onSuccess: () => {
        toast.success("Reserve transaction approved successfully");
        setIsDetailsOpen(false);
        setLoadingRowId(null);
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || "Failed to approve reserve transaction");
        setLoadingRowId(null);
      }
    });
  };

  const handleOpenCancel = (trans) => {
    setCancelTarget(trans);
    setIsCancelOpen(true);
  };

  const handleConfirmCancel = ({ reason }) => {
    if (!cancelTarget) return;
    setLoadingRowId(cancelTarget.uuid);
    cancelTrans(
      { uuid: cancelTarget.uuid, payload: { reason } },
      {
        onSuccess: () => {
          toast.success("Reserve transaction canceled successfully");
          setIsCancelOpen(false);
          setIsDetailsOpen(false);
          setCancelTarget(null);
          setLoadingRowId(null);
        },
        onError: (err) => {
          toast.error(err?.response?.data?.message || "Failed to cancel reserve transaction");
          setLoadingRowId(null);
        }
      }
    );
  };

  const handleConfirmExtend = ({ days }) => {
    if (!extendTarget) return;
    setLoadingRowId(extendTarget.uuid);
    extendTrans(
      { uuid: extendTarget.uuid, payload: { days } },
      {
        onSuccess: () => {
          toast.success(`Reserve transaction extended by ${days} days`);
          setIsExtendOpen(false);
          setIsDetailsOpen(false);
          setExtendTarget(null);
          setLoadingRowId(null);
        },
        onError: (err) => {
          toast.error(err?.response?.data?.message || "Failed to extend reserve transaction");
          setLoadingRowId(null);
        }
      }
    );
  };

  const truncateText = (text, length = 10) => {
    if (!text) return "";
    return text.length > length ? `${text.slice(0, length)}...` : text;
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const columns = [
    {
      accessorKey: "uuid",
      header: "Transaction ID",
      cell: ({ getValue }) => {
        const value = getValue();
        return (
          <div className="flex gap-1 justify-between items-center min-w-[130px]">
            <span title={value}>{truncateText(value, 12)}</span>
            <button onClick={() => handleCopy(value)} className="text-gray-500 hover:text-black">
              <Copy size={14} />
            </button>
          </div>
        );
      },
    },
    {
      accessorKey: "reference",
      header: "Reference",
      cell: ({ getValue }) => {
        const value = getValue();
        return (
          <div className="flex gap-1 justify-between items-center min-w-[130px]">
            <span title={value}>{truncateText(value || "---", 12)}</span>
            <button onClick={() => handleCopy(value)} className="text-gray-500 hover:text-black">
              <Copy size={14} />
            </button>
          </div>
        );
      },
    },
    {
      accessorKey: "serviceType",
      header: "Service Type",
      cell: (info) => info.getValue()
    },
    {
      accessorKey: "created",
      header: "Date",
      cell: (info) => info.getValue() ? new Date(info.getValue()).toLocaleString() : "N/A"
    },
    {
      id: "user",
      header: "User",
      accessorFn: (row) => `${row.user?.firstName || ""} ${row.user?.lastName || ""}`,
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="font-medium text-gray-900">
            {row.original.user?.firstName} {row.original.user?.lastName}
          </span>
          <span className="text-xs text-gray-500">{row.original.user?.email}</span>
        </div>
      ),
    },
    {
      id: "reserveAmount",
      header: "Reserve Amount",
      accessorFn: (row) => `${row.wallet?.currency?.symbol || ""}${row.reserveAmount}`,
      cell: ({ row }) => (
        <span className="font-semibold whitespace-nowrap">
          {row.original.wallet?.currency?.symbol || ""}{" "}
          {Number(row.original.reserveAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      id: "reserveReleaseStatus",
      accessorKey: "reserveReleaseStatus",
      header: "Reserve Release Status",
      cell: (info) => {
        const status = info.getValue();
        const statusStyles = {
          active: "bg-green-100 text-green-800",
          extended: "bg-blue-100 text-blue-800",
          released: "bg-gray-100 text-gray-800",
          cancelled: "bg-red-100 text-red-800",
        };

        return (
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${statusStyles[status?.toLowerCase()] || "bg-gray-100 text-gray-800"
              }`}
          >
            {status?.replace("_", " ")?.toUpperCase() || "---"}
          </span>
        );
      }
    },
    {
      id: "reserveHoldDays",
      accessorKey: "reserveHoldDays",
      header: "Reserve Hold Days",
      cell: (info) => {
        const value = info?.getValue();
        return (

          <span
            className={`px-3 py-1 text-xs font-medium whitespace-nowrap rounded-full`}
          >
            {value !== undefined && value !== null ? value : "N/A"}
          </span>
        );
      }
    },
    {
      id: "reserveReleaseDate",
      accessorKey: "reserveReleaseDate",
      header: "Reserve Release Date",
      cell: (info) => info?.getValue() ? new Date(info?.getValue()).toLocaleString() : "N/A"
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const trans = row.original;
        const isRowLoading = loadingRowId === trans?.uuid;
        const isReleased = trans?.reserveReleaseStatus === "released" || trans?.reserveReleaseStatus === "cancelled";

        const dropdownItems = [
          {
            label: "View Details",
            onClick: () => handleViewDetails(trans),
          },
          ...(!isReleased
            ? [
              {
                label: "Approve",
                onClick: () => handleApprove(trans),
                className: "text-emerald-600",
              },
              {
                label: "Extend Reserve",
                onClick: () => handleOpenExtend(trans),
                className: "text-yellow-600",
              },
              {
                label: "Cancel Reserve",
                onClick: () => handleOpenCancel(trans),
                className: "text-red-600",
              },
            ]
            : []),
        ];

        return (
          <Dropdown
            trigger={
              isRowLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <MoreVertical className="w-4 h-4" />
              )
            }
            items={dropdownItems}
          />
        );
      },
    }
  ];

  const records = useMemo(() => data?.data || [], [data]);

  const totalPages = data?.meta?.totalPages || 1;

  const table = useReactTable({
    data: records,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: totalPages,
    initialState: {
      pagination: {
        pageSize: limit,
        pageIndex: page - 1,
      },
    },
  });

  const handleSearch = (e) => {
    const value = e.target.value;
    setGlobalFilter(value);
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  if (isError) {
    return (
      <div className="flex flex-1 justify-center items-center h-48">
        <p className="px-6 py-4 text-red-600 bg-red-50 rounded-xl border border-red-100">
          Error: {error.message}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="flex justify-between items-center pb-5 bg-white">
        <div className="relative w-80">
          <Search className="absolute left-4 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search by UUID, name or email..."
            className="py-3 pr-4 pl-12 w-full text-base rounded-3xl border border-gray-200 transition-all outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            value={globalFilter}
            onChange={handleSearch}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-[400px]">
          <div className="flex flex-1 justify-center items-center">
            <p className="flex gap-2 items-center font-medium text-gray-600">
              Loading reserve transactions <Loader className="w-5 h-5 animate-spin" />
            </p>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded border border-gray-200">
          <div className="overflow-x-auto min-h-[400px]">
            <table className="w-full">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className="bg-gray-50/50">
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className={`px-4 py-4 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b md:py-4 md:px-6 ${header.column.columnDef.id === "reserveReleaseDate" || header.column.columnDef.id === "reserveHoldDays" || header.column.columnDef.id === "reserveReleaseStatus" ? "min-w-[160px]" : ""}`}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-gray-100 hover:border-b">
                {records.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className="transition-colors hover:bg-gray-50">
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className="px-4 py-3 font-sans text-sm text-gray-600 md:px-6 md:py-5"
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <p className="text-lg font-medium">No records matching your search</p>
                        <p className="text-sm">Try adjusting your filters or search terms</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-4 justify-between items-center p-3 border-t border-gray-200 md:p-6 bg-gray-50/50 md:flex-row">
            <div className="text-sm text-gray-600">
              Showing <span className="font-medium">{(page - 1) * limit + 1}</span> to{" "}
              <span className="font-medium">
                {Math.min(page * limit, data?.meta?.totalItems || 0)}
              </span>{" "}
              of <span className="font-medium">{data?.meta?.totalItems || 0}</span> entries
            </div>
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      )}

      <ReserveTransDetails
        isOpen={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
        data={selectedTrans}
        onApprove={handleApprove}
        onCancel={handleOpenCancel}
        onExtend={handleOpenExtend}
        isApproving={isApproving}
        isCanceling={isCanceling}
      />

      <ExtendReserveModal
        isOpen={isExtendOpen}
        onClose={() => setIsExtendOpen(false)}
        onSubmit={handleConfirmExtend}
        isLoading={isExtending}
        data={extendTarget}
      />

      <CancelReasonModal
        isOpen={isCancelOpen}
        onClose={() => setIsCancelOpen(false)}
        onSubmit={handleConfirmCancel}
        isLoading={isCanceling}
        data={cancelTarget}
      />
    </div>
  );
}

export default ReserveTransTable;
